import { observer } from 'mobx-react-lite'
import {
  Button,
  EnumDropdownItemVariant,
  type IDropdownItem,
  SearchDropdownInput,
  Typography,
} from 'shared/ui'
import { Label } from 'shared/ui/Label'
import { type IResponseEinItem } from 'entities/Compliance'
import { WhereCanIFindBusinessInfoButton } from 'pages/settings/ui/LinkButton/WhereCanIFindBusinessInfoButton'
import { useRcsWizardContext } from 'pages/settings/pages/rcsV2/context/rcsWizardContext'
import { AddressFields } from './fields/AddressFields'
import { BusinessDetailsFields } from './fields/BusinessDetailsFields'

import styles from './styles.module.scss'

const renderEinOption = (einItem: IResponseEinItem) => (
  <div className={styles.einOption}>
    <Typography
      variant='body-md-regular'
      color='var(--content-primary-primary)'
      ariaLabel='FindBusiness_name'
    >
      {einItem.name}
    </Typography>
    <Typography
      variant='body-xs-regular'
      color='var(--content-primary-tertiary)'
      ariaLabel='FindBusiness_address'
    >
      {einItem.street1}
    </Typography>
    <Typography
      variant='body-xs-regular'
      color='var(--content-primary-tertiary)'
      ariaLabel='FindBusiness_ein'
    >
      {`EIN: ${einItem.ein}`}
    </Typography>
  </div>
)

export const FindBusiness = observer(() => {
  const {
    businessInfoStore: {
      einDisplayValue,
      einItems,
      isNoEinResults,
      einError,
      setEinSearch,
      applyEinItem,
      setActiveTab,
      appliedEinItem,
    },
  } = useRcsWizardContext()

  const matchedEinItem = einItems[0]

  const items: IDropdownItem[] = matchedEinItem
    ? [
        {
          id: 0,
          label: matchedEinItem.name,
          className: styles.einItem,
          variant: EnumDropdownItemVariant.Custom,
          renderOption: () => renderEinOption(matchedEinItem),
        },
      ]
    : []

  const onSetValue = (item: IDropdownItem) => {
    const einItem = einItems[Number(item.id)]
    if (einItem) applyEinItem(einItem)
  }

  return (
    <div className={styles.form}>
      <div>
        <Label
          marginBottom={4}
          label={{
            text: 'Employer Identification Number (EIN)',
            rightNode: <WhereCanIFindBusinessInfoButton />,
          }}
        />
        <SearchDropdownInput
          items={items}
          search={einDisplayValue}
          width={492}
          isNoResults={isNoEinResults}
          errorMessageValue={einError}
          tipTitle='Search by EIN, e.g. 12-3456789'
          textFieldProps={{ size: 'medium' }}
          onChangeSearch={setEinSearch}
          onSetValue={onSetValue}
          noResultsMessage={
            <div className={styles.noResults}>
              <Typography
                tag='span'
                variant='body-md-regular'
                color='var(--content-primary-tertiary)'
                ariaLabel='FindBusiness_noResults'
              >
                No results found. If you do not find your business,&nbsp;
              </Typography>
              <Button
                text='enter manually'
                typeBtn='text'
                contained='secondary'
                size='small'
                intent='positive'
                textClassname={styles.enterManuallyButton}
                onClick={() => setActiveTab('manual')}
              />
            </div>
          }
        />
      </div>

      {appliedEinItem && (
        <>
          <AddressFields disabled />
          <BusinessDetailsFields />
        </>
      )}
    </div>
  )
})
